"use client";

/* eslint-disable @next/next/no-img-element */
import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useSpring, useTransform } from "motion/react";
import { Mail, Smartphone, MessageCircle } from "lucide-react";
import { deobfuscateEmailAdvanced, OBFUSCATED_EMAIL_DEV } from "@/app/lib/emailObfuscation";
import { useViewportHeight } from "@/app/hooks/useViewportHeight";
import { springConfig } from "@/app/hooks/useSpringConfig";
import Footer from "../Footer/Footer";

const SCROLL_CONFIG = {
  offset: [0, 0.1, 0.62, 1],
  liftValues: (vh: number) => [vh, 0, 0, -vh],
};

const CONTACT_METHODS = [
  {
    href: "https://wa.me/message/KYD5HAIJADB7H1",
    Icon: MessageCircle,
    label: "WhatsApp",
    isExternal: true,
  },
  {
    href: (email: string) => email ? `mailto:${email}?subject=${encodeURIComponent('Projektanfrage über timomoewes.de')}` : '#',
    Icon: Mail,
    label: "E-Mail",
    requiresEmail: true,
  },
  {
    href: "tel:+4915236124846",
    Icon: Smartphone,
    label: "Telefon",
  },
] as const;
      
const Kontakt = () => {
    const sectionRef = useRef<HTMLDivElement | null>(null);
    const viewportHeight = useViewportHeight();
    const [email, setEmail] = useState<string>("");

    useEffect(() => {
        // Deobfuscate email on client-side
        try {
            const deobfuscated = deobfuscateEmailAdvanced(OBFUSCATED_EMAIL_DEV);
            setEmail(deobfuscated);
        } catch {
            // Silently fail - email will remain empty
        }
    }, []);

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start end", "end start"],
    });

    const liftY = useTransform(
        scrollYProgress,
        SCROLL_CONFIG.offset,
        SCROLL_CONFIG.liftValues(viewportHeight),
        { clamp: false },
    );

    const opacity = useTransform(scrollYProgress, [0, 0.1], [1, 1], { clamp: false });

    const smoothLiftY = useSpring(liftY, springConfig.default);

    const smoothOpacity = useSpring(opacity, springConfig.smooth);

    return (
        <section ref={sectionRef} className="relative w-full">
            <span id="kontakt" className="block h-0 w-0" aria-hidden="true" />
            <motion.div
                initial={false}
                className="fixed inset-0 h-screen w-full flex flex-col items-center justify-start z-[80] pointer-events-none"
                style={{ y: smoothLiftY, opacity: smoothOpacity, background: 'var(--gradient-beige)' }}
            >
                <div className="w-full min-h-screen flex flex-col pointer-events-auto  ">
                    {/* Wave SVG */}
                    <div 
                        className="w-full h-[120px] xs:h-[180px] sm:h-[250px] lg:h-[380px] flex-shrink-0 block m-0 p-0 leading-[0] bg-cover bg-no-repeat"
                        style={{ backgroundImage: "url('/img/wave.svg')" }}
                    />
                    
                    {/* Main Contact Section */}
                    <div
                        className="flex items-center -mt-1 justify-between flex-col flex-1 min-h-[calc(100vh-200px)] sm:min-h-[calc(100vh-300px)] lg:min-h-[calc(100vh-500px)] pb-0"
                        style={{ background: 'var(--gradient-beige-sky)' }}
                    >
                        {/* Main Contact Content */}
                        <div className="kontakt-content-wrapper w-[90%] max-w-[1200px] flex flex-col items-center justify-center gap-2 xs:gap-10 lg:gap-[20px] px-0 xs:px-5 ml-0 lg:ml-[80px] flex-1">
                            {/* Header */}
                            <div className="text-center w-full">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <h3 className="kontakt-header-small text-base xs:text-lg lg:text-[22px] font-bold leading-none tracking-wide text-[var(--dark-blue)] mb-0 sm:mb-1 lg:mb-2 font-[Poppins] uppercase -mt-16 xs:-mt-14 lg:-mt-12">
                                        Kontakt
                                    </h3>
                                <h2
  className="kontakt-header-big text-3xl xs:text-5xl lg:text-[80px] font-normal leading-[0.95] text-[var(--yellow)] text-stroke-lighter
             [--stroke-width:1px] sm:[--stroke-width:2px]"
  style={{
    '--stroke-color': 'var(--dark-blue)',
    textShadow: '4px 4px 0 rgba(29, 43, 79, 0.3)'
  } as React.CSSProperties}
>
                                        Lass uns quatschen!
                                    </h2>
                                </motion.div>
                            </div>

                            {/* Content Grid */}
                            <div className="w-full flex flex-col lg:flex-row items-center lg:items-start justify-center gap-2 xs:gap-10 lg:gap-[50px]">
                                {/* Left: Text and Buttons */}
                                <div className="flex flex-col gap-2 xs:gap-6 lg:gap-8 w-full lg:flex-1 max-w-[600px]">
                                    {/* Text */}
                                    <motion.div 
                                        className="text-center lg:text-left"
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: 0.1 }}
                                    >
                                        <p className="kontakt-text text-[12px] xs:text-base lg:text-lg leading-[13px] sm:leading-[1.7] lg:leading-[1.8] text-[var(--dark-blue)] font-[Poppins] font-medium">
                                            Ich bin immer auf der Suche nach <strong className="font-bold text-[var(--dark-blue)]">spannenden Projekten</strong> und freue mich über eure Nachrichten.
                                            Ob es um ein potenzielles Projekt geht oder einfach nur um einen Austausch – ich antworte so schnell
                                            wie möglich auf deine Anfrage! 🚀
                                        </p>
                                    </motion.div>

                                    {/* Contact Buttons */}
                                    <motion.div 
                                        className="flex flex-wrap items-center justify-center lg:justify-start gap-3 xs:gap-4 lg:gap-5"
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: 0.2 }}
                                    >
                                    {CONTACT_METHODS.map((method) => {
                                        const href = typeof method.href === 'function' ? method.href(email) : method.href;
                                        const { Icon, label } = method;
                                        const isExternal = 'isExternal' in method;
                                        
                                        return (
                                            <motion.a
                                                key={label}
                                                href={href}
                                                className="kontakt-button flex items-center justify-center gap-2 xs:gap-3 px-3 xs:px-5 lg:px-6 py-2 xs:py-3 lg:py-3.5 border-[3px] xs:border-[4px] border-[var(--dark-blue)] rounded-sm shadow-[3px_3px_0_var(--dark-blue)] xs:shadow-[4px_4px_0_var(--dark-blue)] cursor-pointer no-underline text-sm xs:text-base lg:text-lg font-bold font-[Poppins] bg-[var(--yellow)] text-[var(--dark-blue)] transition-colors duration-200 hover:bg-[var(--light-blue)] hover:text-white"
                                                title={`${label} ${isExternal ? 'Nachricht senden' : ''}`}
                                                {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                                                whileHover={{ 
                                                    y: -6,
                                                    rotate: label === 'WhatsApp' ? -1 : label === 'E-Mail' ? 1 : -0.5,
                                                }}
                                                whileTap={{ scale: 0.96 }}
                                                onClick={(e) => {
                                                    if ('requiresEmail' in method && !email) {
                                                        e.preventDefault();
                                                        alert('Email wird geladen...');
                                                    }
                                                }}
                                            >
                                                <Icon size={20} className="kontakt-button-icon w-4 sm:w-5 h-4 sm:h-5 xs:w-6 xs:h-6 flex-shrink-0" />
                                                <span className="whitespace-nowrap text-[11px] xs:text-base">{label}</span>
                                            </motion.a>
                                        );
                                    })}
                                </motion.div>
                                </div>

                                {/* Right: Möwe Image */}
                                <motion.div 
                                    className="kontakt-image w-full max-w-[120px] sm:max-w-[220px] lg:max-w-[280px] flex items-center justify-center transition-transform duration-300"
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: 0.3 }}
                                    whileHover={{ scale: 1.05, rotate: 2 }}
                                >
                                    <img 
                                        src="/img/herzMöwe.svg" 
                                        alt="Möwe mit Herzaugen" 
                                        className="w-full h-auto object-contain"
                                        style={{ filter: 'drop-shadow(3px 3px 0 rgba(29, 43, 79, 0.2))' }}
                                    />
                                </motion.div>
                            </div>
                        </div>
                        
                        {/* Footer Component */}
                        <Footer />
                    </div>
                </div>
            </motion.div>
            <div className="h-[20vh]" aria-hidden="true" />
        </section>
        
    );
};

export default Kontakt;
